import { computed, onMounted, ref, toValue } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { findComponentByType, COMPONENT_TYPES } from 'dashboard/helper/templateHelper';

export const WHATSAPP_TEMPLATE_FAVORITES_KEY = 'whatsapp_template_favorites';

export const getWhatsAppTemplateKey = template =>
  `${template.name}::${template.language || 'en'}`;

const getTemplateSearchText = template => {
  const body = findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
  const category = template.category || '';
  return `${template.name} ${body} ${category}`.toLowerCase();
};

const matchesTeamFilter = (templateKey, templateTeams, teamFilter, userTeamIds) => {
  const assignedTeamIds = templateTeams[templateKey] || [];

  if (!assignedTeamIds.length) {
    return true;
  }

  if (teamFilter === 'my_teams') {
    return assignedTeamIds.some(teamId => userTeamIds.includes(Number(teamId)));
  }

  if (teamFilter && teamFilter !== 'all') {
    return assignedTeamIds.includes(Number(teamFilter));
  }

  return true;
};

export function useWhatsAppTemplatePicker(inboxId, options = {}) {
  const store = useStore();
  const { uiSettings, updateUISettings } = useUISettings();
  const myTeams = useMapGetter('teams/getMyTeams');
  const allTeams = useMapGetter('teams/getTeams');

  const query = ref('');
  const teamFilter = ref('all');
  const showFavoritesOnly = ref(false);

  const resolvedInboxId = computed(() => Number(toValue(inboxId)));

  const inbox = computed(() =>
    store.getters['inboxes/getInbox'](resolvedInboxId.value)
  );

  const templateTeams = computed(() => inbox.value?.template_teams || {});

  const favorites = computed(() => {
    const allFavorites = uiSettings.value[WHATSAPP_TEMPLATE_FAVORITES_KEY] || {};
    return allFavorites[resolvedInboxId.value] || [];
  });

  const userTeamIds = computed(() =>
    myTeams.value.map(team => Number(team.id))
  );

  const isFavorite = template => {
    return favorites.value.includes(getWhatsAppTemplateKey(template));
  };

  const toggleFavorite = (template, event) => {
    event?.stopPropagation();
    event?.preventDefault();

    const templateKey = getWhatsAppTemplateKey(template);
    const allFavorites = {
      ...(uiSettings.value[WHATSAPP_TEMPLATE_FAVORITES_KEY] || {}),
    };
    const inboxFavorites = [...(allFavorites[resolvedInboxId.value] || [])];
    const favoriteIndex = inboxFavorites.indexOf(templateKey);

    if (favoriteIndex >= 0) {
      inboxFavorites.splice(favoriteIndex, 1);
    } else {
      inboxFavorites.push(templateKey);
    }

    allFavorites[resolvedInboxId.value] = inboxFavorites;
    updateUISettings({ [WHATSAPP_TEMPLATE_FAVORITES_KEY]: allFavorites });
  };

  const sortTemplates = templates => {
    return [...templates].sort((left, right) => {
      const leftFavorite = isFavorite(left);
      const rightFavorite = isFavorite(right);

      if (leftFavorite !== rightFavorite) {
        return leftFavorite ? -1 : 1;
      }

      return left.name.localeCompare(right.name);
    });
  };

  const filterTemplates = templates => {
    const normalizedQuery = query.value.trim().toLowerCase();

    return sortTemplates(
      templates.filter(template => {
        const templateKey = getWhatsAppTemplateKey(template);

        if (showFavoritesOnly.value && !favorites.value.includes(templateKey)) {
          return false;
        }

        if (
          !matchesTeamFilter(
            templateKey,
            templateTeams.value,
            teamFilter.value,
            userTeamIds.value
          )
        ) {
          return false;
        }

        if (
          normalizedQuery &&
          !getTemplateSearchText(template).includes(normalizedQuery)
        ) {
          return false;
        }

        return true;
      })
    );
  };

  const teamFilterOptions = computed(() => {
    const options = [
      { value: 'all', labelKey: 'WHATSAPP_TEMPLATES.PICKER.TEAM_FILTER.ALL' },
    ];

    if (userTeamIds.value.length) {
      options.push({
        value: 'my_teams',
        labelKey: 'WHATSAPP_TEMPLATES.PICKER.TEAM_FILTER.MY_TEAMS',
      });
    }

    allTeams.value.forEach(team => {
      options.push({
        value: String(team.id),
        label: team.name,
      });
    });

    return options;
  });

  const processTemplates = sourceTemplates => {
    const templates = toValue(options.templates) ?? sourceTemplates ?? [];

    return (templates || [])
      .filter(template => {
        const status = template.status ? template.status.toLowerCase() : '';
        return !status || status === 'approved';
      })
      .filter(template => Array.isArray(template.components));
  };

  onMounted(() => {
    store.dispatch('teams/get');
  });

  return {
    query,
    teamFilter,
    showFavoritesOnly,
    favorites,
    templateTeams,
    teamFilterOptions,
    filterTemplates,
    isFavorite,
    toggleFavorite,
    processTemplates,
    getWhatsAppTemplateKey,
  };
}
